import { ColumnsType } from "antd/es/table";
import { Landlord, PaginationTable } from "js/components/types";
import React from "react";
import { Button, Card, List, Popconfirm, Table, Tooltip } from "antd";
import { Icon } from "@iconify/react";
import { QuestionCircleOutlined } from "@ant-design/icons";
import LandlordActionSelect from "js/components/Landlords/LandlordTable/LandlordActionSelect";
import { useDeleteLandlord } from "hooks/useLandlords";
import { LandlordsTableProps } from "js/components/Landlords/types";

const LandlordsTable: React.FC<LandlordsTableProps> = ({
  data,
  setData,
  pagination,
  setPagination,
  loading,
  setIsLandlordModalOpen,
  fromOrganization = false,
  screen,
  filteredData,
  legalType,
  setSelectedLandlord,
  messageApi,
}) => {
  const deleteLandlordMutation = useDeleteLandlord();

  const handleDeleteLandlord = (landlord: any) => {
    deleteLandlordMutation.mutate(landlord.id, {
      onSuccess: () => {
        messageApi.success("Propietario eliminado correctamente.");
      },
      onError: () => {
        messageApi.error("No se pudo eliminar el propietario.");
      },
    });
  };

  const columns: ColumnsType<Landlord> =
    legalType === "pf"
      ? [
          {
            title: "Nombre",
            dataIndex: "name",
            render: (name) => <span className="sentry-mask">{name ?? "-"}</span>,
            width: "4%",
          },
          {
            title: "Teléfono",
            dataIndex: "phone_number",
            render: (phone_number) => <span className="sentry-mask">{phone_number ?? "-"}</span>,
            width: "5%",
          },
          {
            title: "Correo",
            dataIndex: "email",
            render: (email) => <span className="sentry-mask">{email ?? "-"}</span>,
            width: "5%",
          },
        ]
      : [
          {
            title: "Nombre",
            dataIndex: "name",
            render: (name) => <span className="sentry-mask">{name ?? "-"}</span>,
            width: "4%",
          },
          {
            title: "Razón Social",
            dataIndex: "legal_entity_name",
            render: (legal_entity_name) => <span className="sentry-mask">{legal_entity_name ?? "-"}</span>,
            width: "8%",
          },
          {
            title: "Nombre Comercial",
            dataIndex: "trade_name",
            render: (trade_name) => <span className="sentry-mask">{trade_name ?? "-"}</span>,
            width: "8%",
          },
          {
            title: "Teléfono",
            dataIndex: "phone_number",
            render: (phone_number) => <span className="sentry-mask">{phone_number ?? "-"}</span>,
            width: "5%",
          },
          {
            title: "Correo",
            dataIndex: "email",
            render: (email) => <span className="sentry-mask">{email ?? "-"}</span>,
            width: "5%",
          },
        ];

  if (!fromOrganization)
    columns.push({
      title: "Acciones",
      dataIndex: "actions",
      align: "center",
      render: (actions, landlord) => (
        <LandlordActionSelect
          landlord={landlord}
          data={data}
          setData={setData}
          setIsLandlordModalOpen={setIsLandlordModalOpen}
          setSelectedLandlord={setSelectedLandlord}
          messageApi={messageApi}
        />
      ),
      width: "5%",
    });

  const handleTableChange = (pagination: PaginationTable) => {
    setPagination(pagination);
  };

  return (
    <>
      {!screen.md ? (
        <>
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 2,
              md: 4,
              lg: 4,
              xl: 4,
              xxl: 3,
            }}
            dataSource={filteredData?.length ? filteredData : data}
            renderItem={(item) => (
              <List.Item>
                <Card
                  title={
                    <span className="sentry-mask">
                      {item.legal_entity_name
                        ? `${item.legal_entity_name}`
                        : `${item.name} ${item.first_surname}`}
                    </span>
                  }
                  actions={[
                    <Tooltip title="Actualizar datos de Propietario">
                      <Button
                        size={"small"}
                        type={"link"}
                        onClick={() => {
                          setIsLandlordModalOpen(true);
                          setSelectedLandlord(item);
                        }}
                      >
                        <Icon fontSize={20} icon="akar-icons:edit" />
                      </Button>
                    </Tooltip>,
                    <Tooltip title="Eliminar Propietario">
                      <Popconfirm
                        title="Eliminar"
                        description="¿Seguro que deseas eliminar?"
                        onConfirm={() => handleDeleteLandlord(item)}
                        icon={
                          <QuestionCircleOutlined
                            style={{ color: "red" }}
                            rev={undefined}
                          />
                        }
                        okText={"Ok"}
                        cancelText={"Cancelar"}
                      >
                        <Button
                          danger
                          size={"small"}
                          type={"link"}
                          loading={deleteLandlordMutation.isPending}
                        >
                          <Icon fontSize={20} icon="bi:trash3" />
                        </Button>
                      </Popconfirm>
                    </Tooltip>,
                  ]}
                >
                  {legalType === "pf" ? (
                    <>
                      <span className="sentry-mask">{item.email}</span>
                      <br />
                      <span className="sentry-mask">{item.phone_number}</span>
                    </>
                  ) : (
                    <>
                      <span className="sentry-mask">{item.name} {item.first_surname} {item.second_surname}</span>
                      <br />
                      <span className="sentry-mask">{item.trade_name}</span>
                      <br />
                      <span className="sentry-mask">{item.email}</span>
                      <br />
                      <span className="sentry-mask">{item.phone_number}</span>
                    </>
                  )}
                </Card>
              </List.Item>
            )}
          />
        </>
      ) : (
        <>
          <Table
            size={"middle"}
            columns={columns}
            rowKey={(record) => record.id ?? ""}
            dataSource={filteredData?.length ? filteredData : data}
            pagination={pagination}
            loading={loading || deleteLandlordMutation.isPending}
            onChange={handleTableChange}
            scroll={{ y: 750, x: "max-content" }}
            components={{
              header: {
                cell: (props: any) => (
                  <th
                    {...props}
                    style={{
                      textAlign: props.children.includes("Acciones")
                        ? "center"
                        : undefined,
                    }}
                  />
                ),
              },
            }}
          />
        </>
      )}
    </>
  );
};

export default LandlordsTable;
